/* eslint-disable max-lines -- tenant/environment config registry: one entry per tenant, splitting it adds indirection */
import packageInfo from '../../../package.json';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN || '';
export const baseEnvironments = [
  'local',
  'develop',
  'staging',
  'production',
  'leap',
  'leapdevelop',
  'agwest',
  'capitalfarmcredit',
  'fcma',
  'compeer',
  'cropgrowers',
  'agloan',
  'agtexas',
  'greenstone',
  'farmcreditmandan',
  'livestockgreenstone',
  'fcsa'
] as const;

export const sentryEnvironments = ['Local', 'Develop', 'Stage', 'Production'] as const;

export const hostnames = [
  'bozic-lia-develop-farmcredit.azurewebsites.net',
  'bozic-lia-stage-farmcredit.azurewebsites.net',
  'cropinsurance.farmcredit.com',
  'lia.agwestfc.com',
  'livestockinsurance.capitalfarmcredit.com',
  'livestock.fcma.com',
  'www.agri-insurance.com',
  'agri-insurance.com',
  'livestockins.compeer.com',
  'livestock.cropgrowers.com',
  'livestockinsurance.agloan.com',
  'livestockinsurance.agtexas.com',
  'livestock.brisk.ag',
  'livestock.farmcreditmandan.com',
  'livestock.greenstone.brisk.ag',
  'livestockinsuranceanalyzer.com'
] as const;

type BaseEnvironment = (typeof baseEnvironments)[number];
type SentryEnvironment = (typeof sentryEnvironments)[number];
type Hostname = (typeof hostnames)[number];

const farmCreditClients = [
  'lia-fcs',
  'lia-compeer',
  'lia-agwestfc',
  'lia-capitalfarmcredit',
  'lia-fcma',
  'lia-agloan',
  'lia-agtexas',
  'lia-mandan',
  'lia-cropgrowers',
  'lia-greenstone',
  'lia-fcsa'
] as const;

export const FARM_CREDIT_CLIENTS = new Set<string>(farmCreditClients);

type FarmCreditClient = (typeof farmCreditClients)[number];
type ClientName = FarmCreditClient | 'lia-leap';

export const clientNameMap: Partial<Record<Hostname, ClientName>> = {
  'cropinsurance.farmcredit.com': 'lia-fcs',
  'lia.agwestfc.com': 'lia-agwestfc',
  'livestockinsurance.capitalfarmcredit.com': 'lia-capitalfarmcredit',
  'livestock.fcma.com': 'lia-fcma',
  'livestockins.compeer.com': 'lia-compeer',
  'livestockinsurance.agloan.com': 'lia-agloan',
  'livestockinsurance.agtexas.com': 'lia-agtexas',
  'www.agri-insurance.com': 'lia-leap',
  'agri-insurance.com': 'lia-leap',
  'livestock.cropgrowers.com': 'lia-cropgrowers',
  'livestock.farmcreditmandan.com': 'lia-mandan',
  'livestock.greenstone.brisk.ag': 'lia-greenstone',
  'livestockinsuranceanalyzer.com': 'lia-fcsa'
};

export interface EnvConfig {
  IsLocal: boolean;
  Name: string;
  PAGE_URL: string;
  AUTH_URL: string;
  INSURANCE_URL: string;
  ORGANIZATION_URL: string;
  CALENDAR_URL: string;
  SMS_MESSAGES_NUMBER: string;
  INSTRUMENTATION_KEY: string;
  APP_ENVIRONMENT: string;
  DISABLE_CACHEBUSTER?: boolean;
  CLIENT_NAME: string;
  CLIENT_ID: string;
  AGENCY_DISPLAY_NAME?: string;
  SENTRY_DSN: string;
  SENTRY_ENVIRONMENT: SentryEnvironment;
  SENTRY_RELEASE: string;
  SENTRY_SERVICE_TAG: string;
}

const releaseName = `${packageInfo.name}-${packageInfo.version}`;

type SharedConfig = Omit<EnvConfig, 'Name'>;
const sharedLocalConfig: SharedConfig = {
  IsLocal: true,
  PAGE_URL: 'http://localhost:3000',
  AUTH_URL: 'https://api.bozic.io/identity',
  INSURANCE_URL: 'https://api.bozic.io/insurance',
  ORGANIZATION_URL: 'https://api.bozic.io/organization',
  CALENDAR_URL: 'https://data.commoditymarkets.com/Web',
  INSTRUMENTATION_KEY: 'e9584812-344d-497b-8a4b-b2a4b8ca4fbe',
  APP_ENVIRONMENT: 'development',
  DISABLE_CACHEBUSTER: true,
  SENTRY_DSN: sentryDsn,
  SENTRY_ENVIRONMENT: 'Local',
  SENTRY_RELEASE: releaseName,
  SMS_MESSAGES_NUMBER: '(612) 324-0802',
  CLIENT_NAME: 'lia-fcs',
  CLIENT_ID: 'lia-fcs-code',
  SENTRY_SERVICE_TAG: 'FrontendFcs'
};
const sharedConfig: Omit<SharedConfig, 'PAGE_URL'> = {
  IsLocal: false,
  AUTH_URL: 'https://api.bozic.io/identity',
  INSURANCE_URL: 'https://api.bozic.io/insurance',
  ORGANIZATION_URL: 'https://api.bozic.io/organization',
  CALENDAR_URL: 'https://data.commoditymarkets.com/web/',
  INSTRUMENTATION_KEY: '7ae470be-2f97-4621-93f2-25225626cc0d',
  APP_ENVIRONMENT: 'production',
  SENTRY_DSN: sentryDsn,
  SENTRY_ENVIRONMENT: 'Production',
  SENTRY_RELEASE: releaseName,
  SMS_MESSAGES_NUMBER: '(612) 324-0802',
  CLIENT_NAME: 'lia-fcs',
  CLIENT_ID: 'lia-fcs-code',
  SENTRY_SERVICE_TAG: 'FrontendFcs'
};

const developLocalConfig: EnvConfig = {
  IsLocal: true,
  Name: 'develop-local',
  PAGE_URL: 'http://localhost:3000',
  AUTH_URL: 'https://bozic-lia-develop-api.azurewebsites.net/identity',
  INSURANCE_URL: 'https://bozic-lia-develop-api.azurewebsites.net/insurance',
  ORGANIZATION_URL: 'https://bozic-lia-develop-api.azurewebsites.net/organization',
  CALENDAR_URL: 'https://udmdirect-data-stage.azurewebsites.net/web/',
  SMS_MESSAGES_NUMBER: '(612) 324-0721',
  INSTRUMENTATION_KEY: 'e9584812-344d-497b-8a4b-b2a4b8ca4fbe',
  APP_ENVIRONMENT: 'development',
  DISABLE_CACHEBUSTER: true,
  CLIENT_NAME: 'lia-fcs',
  CLIENT_ID: 'lia-fcs-code',
  SENTRY_DSN: sentryDsn,
  SENTRY_ENVIRONMENT: 'Local',
  SENTRY_RELEASE: releaseName,
  SENTRY_SERVICE_TAG: 'Frontend'
};

export const environmentConfig = {
  localhost: {
    local: {
      IsLocal: true,
      Name: 'local',
      PAGE_URL: 'http://localhost:3000',
      AUTH_URL: 'https://localhost:5001',
      INSURANCE_URL: 'https://localhost:5005',
      ORGANIZATION_URL: 'https://localhost:5003',
      CALENDAR_URL: 'https://udmdirect-data-stage.azurewebsites.net/web/',
      SMS_MESSAGES_NUMBER: '(612) 324-0721',
      INSTRUMENTATION_KEY: 'e9584812-344d-497b-8a4b-b2a4b8ca4fbe',
      APP_ENVIRONMENT: 'development',
      DISABLE_CACHEBUSTER: true,
      CLIENT_NAME: 'lia-fcs',
      CLIENT_ID: 'lia-fcs-code',
      SENTRY_DSN: sentryDsn,
      SENTRY_ENVIRONMENT: 'Local',
      SENTRY_RELEASE: releaseName,
      SENTRY_SERVICE_TAG: 'Frontend'
    },
    develop: developLocalConfig,
    // LEAP tenant identity against the develop API — the only mode agents may use to verify
    // LEAP behavior locally (localhost.leap targets the production api.bozic.io).
    leapdevelop: {
      ...developLocalConfig,
      Name: 'leapdevelop-local',
      CLIENT_NAME: 'lia-leap',
      CLIENT_ID: 'lia-leap-code',
      SENTRY_SERVICE_TAG: 'FrontendLeap'
    },
    staging: {
      IsLocal: true,
      Name: 'staging-local',
      PAGE_URL: 'http://localhost:3000',
      AUTH_URL: 'https://bozic-lia-stage-api.azurewebsites.net/identity',
      INSURANCE_URL: 'https://bozic-lia-stage-api.azurewebsites.net/insurance',
      ORGANIZATION_URL: 'https://bozic-lia-stage-api.azurewebsites.net/organization',
      CALENDAR_URL: 'https://udmdirect-data-stage.azurewebsites.net/web/',
      SMS_MESSAGES_NUMBER: '(612) 324-0721',
      INSTRUMENTATION_KEY: 'e9584812-344d-497b-8a4b-b2a4b8ca4fbe',
      APP_ENVIRONMENT: 'development',
      DISABLE_CACHEBUSTER: true,
      CLIENT_NAME: 'lia-fcs',
      CLIENT_ID: 'lia-fcs-code',
      SENTRY_DSN: sentryDsn,
      SENTRY_ENVIRONMENT: 'Local',
      SENTRY_RELEASE: releaseName,
      SENTRY_SERVICE_TAG: 'Frontend'
    },
    production: {
      ...sharedLocalConfig,
      Name: 'production-local'
    },
    agwest: {
      ...sharedLocalConfig,
      Name: 'agwest-local'
    },
    capitalfarmcredit: {
      ...sharedLocalConfig,
      Name: 'capitalfarmcredit-local'
    },
    fcma: {
      ...sharedLocalConfig,
      Name: 'fcma-local'
    },
    compeer: {
      ...sharedLocalConfig,
      Name: 'compeer-local'
    },
    cropgrowers: {
      ...sharedLocalConfig,
      Name: 'cropgrowers-local'
    },
    agloan: {
      ...sharedLocalConfig,
      Name: 'agloan-local'
    },
    greenstone: {
      ...sharedLocalConfig,
      Name: 'greenstone-local'
    },
    agtexas: {
      ...sharedLocalConfig,
      Name: 'agtexas-local'
    },
    farmcreditmandan: {
      ...sharedLocalConfig,
      Name: 'farmcreditmandan-local'
    },
    livestockgreenstone: {
      ...sharedLocalConfig,
      Name: 'livestockgreenstone-local'
    },
    fcsa: {
      ...sharedLocalConfig,
      Name: 'fcsa-local'
    },
    leap: {
      ...sharedLocalConfig,
      Name: 'leap-local',
      SMS_MESSAGES_NUMBER: '(612) 324-0721',
      CLIENT_NAME: 'lia-leap',
      CLIENT_ID: 'lia-leap-code',
      SENTRY_SERVICE_TAG: 'FrontendLeap'
    }
  },
  remote: {
    local: {} as EnvConfig,
    // Local-verification-only mode; there is no deployed leap-develop host.
    leapdevelop: {} as EnvConfig,
    develop: {
      IsLocal: false,
      Name: 'develop',
      PAGE_URL: 'https://bozic-lia-develop-farmcredit.azurewebsites.net',
      AUTH_URL: 'https://bozic-lia-develop-api.azurewebsites.net/identity',
      INSURANCE_URL: 'https://bozic-lia-develop-api.azurewebsites.net/insurance',
      ORGANIZATION_URL: 'https://bozic-lia-develop-api.azurewebsites.net/organization',
      CALENDAR_URL: 'https://udmdirect-data-stage.azurewebsites.net/web/',
      SMS_MESSAGES_NUMBER: '(612) 324-0721',
      INSTRUMENTATION_KEY: 'e9584812-344d-497b-8a4b-b2a4b8ca4fbe',
      APP_ENVIRONMENT: 'development',
      CLIENT_NAME: 'lia-fcs',
      CLIENT_ID: 'lia-fcs-code',
      SENTRY_DSN: sentryDsn,
      SENTRY_ENVIRONMENT: 'Develop',
      SENTRY_RELEASE: releaseName,
      SENTRY_SERVICE_TAG: 'Frontend'
    },
    staging: {
      IsLocal: false,
      Name: 'staging',
      PAGE_URL: 'https://bozic-lia-stage-farmcredit.azurewebsites.net',
      AUTH_URL: 'https://bozic-lia-stage-api.azurewebsites.net/identity',
      INSURANCE_URL: 'https://bozic-lia-stage-api.azurewebsites.net/insurance',
      ORGANIZATION_URL: 'https://bozic-lia-stage-api.azurewebsites.net/organization',
      CALENDAR_URL: 'https://udmdirect-data-stage.azurewebsites.net/web/',
      SMS_MESSAGES_NUMBER: '(612) 324-7695',
      INSTRUMENTATION_KEY: '2a90f911-b758-4921-87b5-64f583e782c2',
      APP_ENVIRONMENT: 'production',
      CLIENT_NAME: 'lia-fcs',
      CLIENT_ID: 'lia-fcs-code',
      SENTRY_DSN: sentryDsn,
      SENTRY_ENVIRONMENT: 'Stage',
      SENTRY_RELEASE: releaseName,
      SENTRY_SERVICE_TAG: 'Frontend'
    },
    production: {
      ...sharedConfig,
      Name: 'production',
      PAGE_URL: 'https://cropinsurance.farmcredit.com',
      AGENCY_DISPLAY_NAME: 'Farm Credit Services'
    },
    agwest: {
      ...sharedConfig,
      Name: 'agwest',
      PAGE_URL: 'https://lia.agwestfc.com',
      AGENCY_DISPLAY_NAME: 'AgWest Farm Credit'
    },
    capitalfarmcredit: {
      ...sharedConfig,
      Name: 'capitalfarmcredit',
      PAGE_URL: 'https://livestockinsurance.capitalfarmcredit.com',
      AGENCY_DISPLAY_NAME: 'Capital Farm Credit'
    },
    fcma: {
      ...sharedConfig,
      Name: 'fcma',
      PAGE_URL: 'https://livestock.fcma.com',
      AGENCY_DISPLAY_NAME: 'Farm Credit Mid-America'
    },
    compeer: {
      ...sharedConfig,
      Name: 'compeer',
      PAGE_URL: 'https://livestockins.compeer.com',
      AGENCY_DISPLAY_NAME: 'Compeer Financial'
    },
    cropgrowers: {
      ...sharedConfig,
      Name: 'cropgrowers',
      PAGE_URL: 'https://livestock.cropgrowers.com',
      AGENCY_DISPLAY_NAME: 'CropGrowers'
    },
    greenstone: {
      ...sharedConfig,
      Name: 'greenstone',
      PAGE_URL: 'https://livestock.brisk.ag',
      AGENCY_DISPLAY_NAME: 'GreenStone Farm Credit Services'
    },
    agloan: {
      ...sharedConfig,
      Name: 'agloan',
      PAGE_URL: 'https://livestockinsurance.agloan.com',
      AGENCY_DISPLAY_NAME: 'American AgCredit, FLCA'
    },
    agtexas: {
      ...sharedConfig,
      Name: 'agtexas',
      PAGE_URL: 'https://livestockinsurance.agtexas.com',
      AGENCY_DISPLAY_NAME: 'AgTexas Farm Credit Services'
    },
    farmcreditmandan: {
      ...sharedConfig,
      Name: 'farmcreditmandan',
      PAGE_URL: 'https://livestock.farmcreditmandan.com',
      AGENCY_DISPLAY_NAME: 'Farm Credit Mandan'
    },
    livestockgreenstone: {
      ...sharedConfig,
      Name: 'livestockgreenstone',
      PAGE_URL: 'https://livestock.greenstone.brisk.ag',
      AGENCY_DISPLAY_NAME: 'GreenStone Farm Credit Services'
    },
    fcsa: {
      ...sharedConfig,
      Name: 'fcsa',
      PAGE_URL: 'https://livestockinsuranceanalyzer.com',
      AGENCY_DISPLAY_NAME: 'Farm Credit Services of America'
    },
    leap: {
      ...sharedConfig,
      Name: 'leap',
      PAGE_URL: 'https://agri-insurance.com',
      SMS_MESSAGES_NUMBER: '(612) 324-0721',
      CLIENT_NAME: 'lia-leap',
      CLIENT_ID: 'lia-leap-code',
      AGENCY_DISPLAY_NAME: 'AgStrat',
      SENTRY_SERVICE_TAG: 'FrontendLeap'
    }
  }
} satisfies {
  localhost: Record<BaseEnvironment, EnvConfig>;
  remote: Record<BaseEnvironment, EnvConfig>;
};

const hostnameMap: Record<Hostname, EnvConfig> = {
  'bozic-lia-develop-farmcredit.azurewebsites.net': environmentConfig.remote.develop,
  'bozic-lia-stage-farmcredit.azurewebsites.net': environmentConfig.remote.staging,
  'cropinsurance.farmcredit.com': environmentConfig.remote.production,
  'lia.agwestfc.com': environmentConfig.remote.agwest,
  'livestockinsurance.capitalfarmcredit.com': environmentConfig.remote.capitalfarmcredit,
  'livestock.fcma.com': environmentConfig.remote.fcma,
  'www.agri-insurance.com': environmentConfig.remote.leap,
  'agri-insurance.com': environmentConfig.remote.leap,
  'livestockins.compeer.com': environmentConfig.remote.compeer,
  'livestock.cropgrowers.com': environmentConfig.remote.cropgrowers,
  'livestockinsurance.agloan.com': environmentConfig.remote.agloan,
  'livestockinsurance.agtexas.com': environmentConfig.remote.agtexas,
  'livestock.brisk.ag': environmentConfig.remote.greenstone,
  'livestock.farmcreditmandan.com': environmentConfig.remote.farmcreditmandan,
  'livestock.greenstone.brisk.ag': environmentConfig.remote.livestockgreenstone,
  'livestockinsuranceanalyzer.com': environmentConfig.remote.fcsa
};

export function getConfig(): EnvConfig | undefined {
  const { hostname } = window.location;

  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    const mode = import.meta.env.MODE;
    const environment = mode.split('-')[0] as BaseEnvironment;
    return environmentConfig.localhost[environment];
  }

  if (hostname in hostnameMap) {
    const config = hostnameMap[hostname as Hostname];
    const clientName = clientNameMap[hostname as Hostname];

    if (clientName) {
      return {
        ...config,
        CLIENT_NAME: clientName,
        CLIENT_ID: `${clientName}-code`
      };
    }

    return config;
  }

  throw new Error(`Unknown environment for hostname: ${hostname}`);
}
